/**
 * <ia-reviews>, the card around the Yotpo reviews widget (sections/ia-reviews).
 *
 * Yotpo prints a review's stars alone; the design wants the score as a number
 * beside them. The number is read from the stars' own aria-label and added
 * after every render (a filter, a page, a tab rebuilds the panel).
 *
 * A page change leaves the shopper at the bottom of the list, where the
 * pagination sits; once the new page has rendered, the list goes back to its
 * first item: the card's own scroller on desktop, the page below the header
 * where the list is in flow.
 */
const STARS = '.yotpo-review .yotpo-review-star-rating';
const PAGINATION = '.yotpo-pagination-item, .yotpo-pagination-number, .yotpo-pagination-icon';
const ITEM = '.yotpo-review, .yotpo-question';
const SCROLLER = '.yotpo-reviews-container, .yotpo-questions-container';
// A page that never arrives (a failed fetch) must not scroll a later render.
const PAGE_WAIT_MS = 4000;

class IaReviews extends HTMLElement {
  connectedCallback() {
    this.onClick = this.handleClick.bind(this);
    // Capture: Yotpo's pagination stops the click before it bubbles.
    this.addEventListener('click', this.onClick, true);
    this.observer = new MutationObserver(() => this.queue());
    this.observer.observe(this, { childList: true, subtree: true });
    this.queue();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick, true);
    this.observer?.disconnect();
    clearTimeout(this.timer);
    clearTimeout(this.pageTimer);
  }

  /* Both panels stay in the DOM (the hidden tab too), so the list that owns
     the clicked pagination is the one watched. */
  handleClick(event) {
    const control = event.target.closest(PAGINATION);
    if (!control) return;
    this.pageScope = control.closest(SCROLLER) || this;
    this.pageFrom = this.pageScope.querySelector(ITEM);
    clearTimeout(this.pageTimer);
    this.pageTimer = setTimeout(() => { this.pageFrom = null; }, PAGE_WAIT_MS);
  }

  /* The new page is in when the first item is another node. */
  settlePage() {
    if (!this.pageFrom) return;
    const scope = this.pageScope?.isConnected ? this.pageScope : this;
    const first = scope.querySelector(ITEM);
    if (!first || first === this.pageFrom) return;
    this.pageFrom = null;
    clearTimeout(this.pageTimer);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    const scroller = first.closest(SCROLLER);
    if (scroller && scroller.scrollHeight > scroller.clientHeight) {
      scroller.scrollTo({ top: 0, behavior });
    }
    const top = first.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight) {
      first.style.scrollMarginTop = 'calc(var(--ia-header-height, 0px) + 16px)';
      first.scrollIntoView({ behavior, block: 'start' });
    }
  }

  /* One pass per burst of mutations: Yotpo paints in many small steps. */
  queue() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.dress(), 50);
  }

  dress() {
    this.querySelectorAll(STARS).forEach((stars) => this.numberStars(stars));
    this.settlePage();
  }

  numberStars(stars) {
    if (stars.previousElementSibling?.classList.contains('ia-reviews__score')) return;
    const match = /(\d+(?:\.\d+)?)\s+out of\s+(\d+)/.exec(stars.getAttribute('aria-label') || stars.title || '');
    if (!match) return;
    const score = document.createElement('span');
    score.className = 'ia-reviews__score';
    score.setAttribute('aria-hidden', 'true');
    score.textContent = `${match[1]}/${match[2]}`;
    stars.before(score);
  }
}

if (!customElements.get('ia-reviews')) {
  customElements.define('ia-reviews', IaReviews);
}
